import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { TrendingUp, Users, Package, Calendar, BookOpen, IndianRupee, ArrowUpRight } from "lucide-react";
import AdminRevenue from "@/components/AdminRevenue";
import AdminSettings from "@/components/AdminSettings";
import SmsStatusCard from "@/components/SmsStatusCard";
import NotificationModeCard from "@/components/NotificationModeCard";
import PaymentSettingsCard from "@/components/PaymentSettingsCard";
import EmailSettingsCard from "@/components/EmailSettingsCard";
import SetupChecklist from "@/components/admin/SetupChecklist";
import ChangePasswordCard from "@/components/ChangePasswordCard";
import ProductsTab from "@/components/admin/ProductsTab";
import YogaTab from "@/components/admin/YogaTab";
import OffersTab from "@/components/admin/OffersTab";
import ShippingTab from "@/components/admin/ShippingTab";
import AppointmentsTab from "@/components/admin/AppointmentsTab";
import OrdersTab from "@/components/admin/OrdersTab";
import SamhitaTab from "@/components/admin/SamhitaTab";
import PrescriptionsTab from "@/components/admin/PrescriptionsTab";
import OverviewTab from "@/components/admin/OverviewTab";
import AvailabilityTab from "@/components/admin/AvailabilityTab";
import ContentTab from "@/components/admin/ContentTab";
import GurusTab from "@/components/admin/GurusTab";
import CommunityTab from "@/components/admin/CommunityTab";
import GarbhaTab from "@/components/admin/GarbhaTab";
import PatientsTab from "@/components/admin/PatientsTab";
import SubscribersTab from "@/components/admin/SubscribersTab";
import BroadcastTab from "@/components/admin/BroadcastTab";
import RetreatsTab from "@/components/admin/RetreatsTab";
import RxUploadsTab from "@/components/admin/RxUploadsTab";
import ReferralSourcesTab from "@/components/admin/ReferralSourcesTab";
import DoctorLeaderboard from "@/components/admin/DoctorLeaderboard";
import ReportsTab from "@/components/admin/ReportsTab";
import DoctorPartnersTab from "@/components/admin/DoctorPartnersTab";
import DoctorProductsTab from "@/components/admin/DoctorProductsTab";

const StatCard = ({ icon: Icon, label, val, color, onClick, testid }) => (
  <Card
    role="button"
    tabIndex={0}
    onClick={onClick}
    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } }}
    data-testid={testid}
    title={`Open ${label}`}
    className="border-border cursor-pointer transition-all hover:border-primary/50 hover:-translate-y-0.5 hover:shadow-[0_12px_28px_-18px_rgba(33,83,52,0.55)] active:translate-y-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 group"
  >
    <CardContent className="p-5">
      <div className="flex items-start justify-between">
        <div className={`w-10 h-10 rounded-lg ${color} flex items-center justify-center mb-3`}><Icon size={18}/></div>
        <ArrowUpRight size={15} className="text-muted-foreground/40 group-hover:text-primary transition-colors" />
      </div>
      <div className="font-display text-3xl">{val}</div>
      <div className="text-xs text-muted-foreground uppercase tracking-wider group-hover:text-primary transition-colors">{label}</div>
    </CardContent>
  </Card>
);

// One row per tab: [value, label, data-testid]. Rendered as full-size pill buttons so
// every section is an obvious, comfortably tappable target on phones and laptops alike.
const TABS = [
  ["overview", "Overview", "adm-tab-overview"],
  ["setup", "Setup", "adm-tab-setup"],
  ["patients", "Patients", "adm-tab-patients"],
  ["products", "Products", "adm-tab-products"],
  ["appointments", "Appointments", "adm-tab-appts"],
  ["availability", "Availability", "adm-tab-availability"],
  ["orders", "Orders", "adm-tab-orders"],
  ["offers", "Offers", "adm-tab-offers"],
  ["subscribers", "Subscribers", "adm-tab-subscribers"],
  ["broadcast", "Broadcast", "adm-tab-broadcast"],
  ["retreats", "Retreats", "adm-tab-retreats"],
  ["revenue", "Revenue & GST", "adm-tab-revenue"],
  ["samhita", "Samhita", "adm-tab-samhita"],
  ["community", "Community", "adm-tab-community"],
  ["yoga", "Yoga", "adm-tab-yoga"],
  ["gurus", "Gurus", "adm-tab-gurus"],
  ["garbha", "Garbhsanskar", "adm-tab-garbha"],
  ["content", "Site Content", "adm-tab-content"],
  ["prescriptions", "Prescriptions", "adm-tab-rx"],
  ["rx-uploads", "Rx Uploads", "adm-tab-rx-uploads"],
  ["referrals", "Referral sources", "adm-tab-referrals"],
  ["leaderboard", "Doctor leaderboard", "adm-tab-leaderboard"],
  ["reports", "Reports", "adm-tab-reports"],
  ["partners", "Doctor partners", "adm-tab-partners"],
  ["doctor-products", "Doctor products & payouts", "adm-tab-doctor-products"],
  ["shipping", "Shipping & Courier", "adm-tab-shipping"],
  ["settings", "Settings", "adm-tab-settings"],
];

export default function Admin() {
  const [stats, setStats] = useState({});
  const [tab, setTab] = useState("overview");
  const [products, setProducts] = useState([]);
  const [appts, setAppts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [samhita, setSamhita] = useState([]);
  const [users, setUsers] = useState([]);

  const refresh = () => {
    api.get("/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/products").then(({ data }) => setProducts(data));
    api.get("/appointments").then(({ data }) => setAppts(data));
    api.get("/orders").then(({ data }) => setOrders(data));
    api.get("/samhita").then(({ data }) => setSamhita(data));
    api.get("/users").then(({ data }) => setUsers(data));
  };
  useEffect(() => { refresh(); }, []);

  // Every summary card is a shortcut into the section that holds the real records.
  const openTab = (value) => {
    setTab(value);
    requestAnimationFrame(() => {
      const el = document.getElementById("adm-sections");
      if (!el) return;
      // Clear the sticky promo bar and navigation so the section starts in view.
      window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 120, behavior: "smooth" });
    });
  };

  return (
    <main className="max-w-7xl mx-auto px-6 lg:px-12 py-10">
      <div className="overline mb-2">Admin Panel</div>
      <h1 className="font-display text-4xl lg:text-5xl mb-2">Practice Control Room</h1>
      <p className="text-sm text-muted-foreground mb-8">Tap any figure below to open the records behind it.</p>

      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 mb-10">
        <StatCard icon={Users} label="Patients" val={stats.users || 0} color="bg-emerald-50 text-primary"
                  onClick={() => openTab("patients")} testid="stat-card-patients"/>
        <StatCard icon={Calendar} label="Appointments" val={stats.appointments || 0} color="bg-amber-50 text-secondary"
                  onClick={() => openTab("appointments")} testid="stat-card-appointments"/>
        <StatCard icon={Package} label="Products" val={stats.products || 0} color="bg-rose-50 text-primary"
                  onClick={() => openTab("products")} testid="stat-card-products"/>
        <StatCard icon={TrendingUp} label="Orders" val={stats.orders || 0} color="bg-sky-50 text-primary"
                  onClick={() => openTab("orders")} testid="stat-card-orders"/>
        <StatCard icon={IndianRupee} label="Revenue" val={`₹${(stats.revenue || 0).toFixed(0)}`} color="bg-lime-50 text-primary"
                  onClick={() => openTab("revenue")} testid="stat-card-revenue"/>
        <StatCard icon={BookOpen} label="Library" val={stats.samhita || 0} color="bg-orange-50 text-secondary"
                  onClick={() => openTab("samhita")} testid="stat-card-library"/>
      </div>

      <Tabs value={tab} onValueChange={setTab} id="adm-sections">
        <TabsList className="bg-muted/60 flex h-auto w-full flex-wrap justify-start gap-2 p-2 rounded-2xl" data-testid="adm-tablist">
          {TABS.map(([value, label, testid]) => (
            <TabsTrigger
              key={value}
              value={value}
              data-testid={testid}
              className="h-11 shrink-0 rounded-full border border-border/60 bg-background/50 px-4 text-sm text-foreground/70 transition-colors hover:border-primary/40 hover:bg-background hover:text-primary data-[state=active]:border-primary data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm"
            >
              {label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="overview" className="mt-6">
          <OverviewTab />
        </TabsContent>

        <TabsContent value="availability" className="mt-6">
          <AvailabilityTab />
        </TabsContent>

        <TabsContent value="gurus" className="mt-6">
          <GurusTab />
        </TabsContent>

        <TabsContent value="garbha" className="mt-6">
          <GarbhaTab />
        </TabsContent>

        <TabsContent value="content" className="mt-6">
          <ContentTab />
        </TabsContent>

        <TabsContent value="setup" className="mt-6">
          <SetupChecklist />
        </TabsContent>

        <TabsContent value="patients" className="mt-6">
          <PatientsTab users={users} orders={orders} appointments={appts} />
        </TabsContent>

        <TabsContent value="products" className="mt-6">
          <ProductsTab products={products} refresh={refresh}/>
        </TabsContent>

        <TabsContent value="appointments" className="mt-6">
          <AppointmentsTab appointments={appts} refresh={refresh}/>
        </TabsContent>

        <TabsContent value="orders" className="mt-6">
          <OrdersTab orders={orders} refresh={refresh}/>
        </TabsContent>

        <TabsContent value="offers" className="mt-6">
          <OffersTab />
        </TabsContent>

        <TabsContent value="subscribers" className="mt-6">
          <SubscribersTab />
        </TabsContent>

        <TabsContent value="broadcast" className="mt-6">
          <BroadcastTab />
        </TabsContent>

        <TabsContent value="retreats" className="mt-6">
          <RetreatsTab />
        </TabsContent>

        <TabsContent value="revenue" className="mt-6">
          <AdminRevenue />
        </TabsContent>

        <TabsContent value="shipping" className="mt-6">
          <ShippingTab />
        </TabsContent>

        <TabsContent value="settings" className="mt-6 space-y-5">
          <AdminSettings />
          <NotificationModeCard />
          <PaymentSettingsCard />
          <EmailSettingsCard />
          <SmsStatusCard />
          <ChangePasswordCard />
        </TabsContent>

        <TabsContent value="samhita" className="mt-6">
          <SamhitaTab samhita={samhita} refresh={refresh}/>
        </TabsContent>

        <TabsContent value="community" className="mt-6">
          <CommunityTab />
        </TabsContent>

        <TabsContent value="yoga" className="mt-6">
          <YogaTab />
        </TabsContent>

        <TabsContent value="prescriptions" className="mt-6">
          <PrescriptionsTab users={users}/>
        </TabsContent>

        <TabsContent value="rx-uploads" className="mt-6">
          <RxUploadsTab />
        </TabsContent>

        <TabsContent value="referrals" className="mt-6">
          <ReferralSourcesTab />
        </TabsContent>

        <TabsContent value="leaderboard" className="mt-6">
          <DoctorLeaderboard />
        </TabsContent>

        <TabsContent value="reports" className="mt-6">
          <ReportsTab />
        </TabsContent>

        <TabsContent value="partners" className="mt-6">
          <DoctorPartnersTab />
        </TabsContent>

        <TabsContent value="doctor-products" className="mt-6">
          <DoctorProductsTab />
        </TabsContent>
      </Tabs>
    </main>
  );
}
